import styled from "@emotion/styled";
import { CartCost, useCart } from "@shopify/hydrogen-react";
import {
  Box,
  Callout,
  Container,
  FlexRow,
  H1,
  H2,
  IconArrow,
  IconCart,
  Link,
  List,
  Stack,
  Text,
} from "atoms";
import { paths } from "utils/paths";
import { trackEvent } from "contexts/mixpanel";
import { CartLineItem } from "./CartLineItem";
import { type LineItem, isLineItem } from "../utils/isLineItem";

const trackCheckOutClick = (subtotal?: string) => {
  // Cost As Decimal
  const cartSubtotal = parseFloat(subtotal ?? "0.0");

  // Send to Mix Panel
  trackEvent({ name: "CheckoutShopify", cartSubtotal });
};

function CartDetails() {
  const {
    lines,
    linesUpdate,
    linesRemove,
    cost,
    totalQuantity = 0,
    checkoutUrl,
  } = useCart();

  const createQtyOnChange =
    ({ id, attributes }: LineItem) =>
    (quantity: number) =>
      linesUpdate([{ id, attributes, quantity }]);

  const createLineRemove = (id: string) => () => linesRemove([id]);

  return (
    <CartContainer width="sm">
      <Stack gap="sm">
        <FlexRow justifyContent="space-between" alignItems="baseline">
          <H1 size="xl" style={{ margin: 0 }}>
            <IconCart size=".8em" /> Cart
          </H1>

          {checkoutUrl && totalQuantity > 0 && (
            <SText bold>
              <Link size="xs" href={checkoutUrl} variant="blank">
                Checkout <IconArrow dir="r" />
              </Link>
            </SText>
          )}
        </FlexRow>

        <Stack gap="sm">
          {totalQuantity ? (
            <SBox fullWidth>
              <Stack gap="1.5rem">
                {lines && (
                  <CartItemList type="blank" gap="1.5em">
                    {lines.filter(isLineItem).map((line) => (
                      <CartLineItem
                        key={line.id}
                        {...line}
                        onChange={createQtyOnChange(line)}
                        remove={createLineRemove(line.id)}
                      />
                    ))}
                  </CartItemList>
                )}

                <Totals gap="xxs">
                  <TotalsGrid>
                    {cost?.subtotalAmount && (
                      <>
                        <SText bold style={{ color: "#262626" }}>
                          Subtotal
                        </SText>
                        <SText bold style={{ color: "#262626" }}>
                          <SCartCost amountType="subtotal" as="span" />
                        </SText>
                      </>
                    )}

                    {cost?.totalTaxAmount && (
                      <>
                        <SText>Tax</SText>
                        <SCartCost amountType="tax" />
                      </>
                    )}
                  </TotalsGrid>

                  <SText size="xxs" style={{ lineHeight: 1.2 }}>
                    Shipping and taxes are calculated at checkout.
                  </SText>
                </Totals>

                {checkoutUrl && totalQuantity > 0 && (
                  <FlexRow justifyContent="flex-end">
                    <SLink
                      href={checkoutUrl}
                      variant="primary"
                      onClick={() =>
                        trackCheckOutClick(cost?.subtotalAmount?.amount)
                      }
                    >
                      Checkout
                    </SLink>
                  </FlexRow>
                )}
              </Stack>
            </SBox>
          ) : (
            <Callout>
              <Stack gap="sm">
                <Stack>
                  <H2 size="lg" textTransform="none" textAlign="center">
                    Your cart is empty!
                  </H2>
                  <Text textAlign="center">
                    Go ahead and get yourself something nice 😊
                  </Text>
                </Stack>

                <FlexRow justifyContent="center">
                  <Link to={paths.shop} variant="primary">
                    Visit the Shop
                  </Link>
                </FlexRow>
              </Stack>
            </Callout>
          )}
        </Stack>
      </Stack>
    </CartContainer>
  );
}

const CartContainer = styled(Container)({
  paddingTop: "1.25rem",
  paddingBottom: "4rem",
});

const CartItemList = styled(Stack)({
  margin: 0,
  padding: 0,
}).withComponent(List);

const SCartCost = styled(CartCost)({
  fontVariantNumeric: "tabular-nums",
});

const SText = styled(Text)({
  margin: 0,
});

const SBox = styled(Box)(({ theme }) => ({
  [theme.breakpoints.maxTablet]: {
    paddingTop: "2rem",
  },
}));

const SLink = styled(Link)(({ theme }) => ({
  whiteSpace: "nowrap",
  flex: "0 0 100%",
  [theme.breakpoints.xs]: {
    flex: "0 0 auto",
    minWidth: 150,
  },
}));

const Totals = styled(Stack)(({ theme }) => ({
  marginLeft: "auto",
  [theme.breakpoints.tablet]: { maxWidth: "max-content" },
}));

const TotalsGrid = styled.div({
  gap: "2px 2rem",
  display: "grid",
  gridTemplateColumns: "1fr auto",
});

export { CartDetails };
